import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { isNative, platform, openExternal } from "../lib/native";
import { Button } from "./ui/button";
import { AlertTriangle, ArrowUpCircle, X } from "lucide-react";

/**
 * Force-update gate + soft-update nudge.
 *
 * Wraps the entire app. On native (iOS/Android) it fetches
 * `GET /api/app/version` on mount and compares the installed Capacitor
 * version against two thresholds:
 *   - `min_version`     → HARD BLOCK. Renders a full-screen modal that
 *                         hides every route until the user updates.
 *   - `latest_version`  → SOFT NUDGE. Renders a small dismissible banner
 *                         above the app while everything stays usable.
 *                         Dismissal is keyed to `latest_version` so a
 *                         newer release re-shows the banner.
 *
 * On web (Capacitor.isNativePlatform() === false) the gate is a no-op
 * and simply renders its children.
 *
 * Contract:
 *   - Fail-open. If the endpoint / plugin errors, render children.
 *   - Semver-aware compare (`isVersionBelow`).
 *   - Banner never appears on top of the hard-block modal.
 */
export default function ForceUpdateGate({ children }) {
  const [decision, setDecision] = useState({ state: "checking" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isNative()) {
        if (!cancelled) setDecision({ state: "ok" });
        return;
      }
      try {
        const { App: CapApp } = await import("@capacitor/app");
        const info = await CapApp.getInfo();
        const installed = info?.version || "";
        const { data } = await api.get("/app/version");
        const plat = platform();
        const cfg = data?.[plat];
        if (!cfg || !cfg.min_version) {
          if (!cancelled) setDecision({ state: "ok" });
          return;
        }
        // Hard block first — takes precedence.
        if (isVersionBelow(installed, cfg.min_version)) {
          if (!cancelled) setDecision({
            state: "blocked",
            storeUrl: cfg.store_url,
            installed,
            required: cfg.min_version,
            platform: plat,
          });
          return;
        }
        // Soft nudge — installed is above min but below latest.
        const latest = cfg.latest_version || cfg.min_version;
        if (isVersionBelow(installed, latest)) {
          if (!cancelled) setDecision({
            state: "nudge",
            storeUrl: cfg.store_url,
            installed,
            latest,
            platform: plat,
          });
          return;
        }
        if (!cancelled) setDecision({ state: "ok" });
      } catch (e) {
        if (!cancelled) setDecision({ state: "ok" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (decision.state === "checking" && isNative()) {
    return <div data-testid="force-update-checking" className="fixed inset-0 bg-[#0E1F16]" />;
  }
  if (decision.state === "blocked") {
    return (
      <UpdateBlockedScreen
        storeUrl={decision.storeUrl}
        installed={decision.installed}
        required={decision.required}
        platform={decision.platform}
      />
    );
  }
  return (
    <>
      {decision.state === "nudge" && (
        <SoftUpdateBanner
          storeUrl={decision.storeUrl}
          installed={decision.installed}
          latest={decision.latest}
          platform={decision.platform}
        />
      )}
      {children}
    </>
  );
}

function UpdateBlockedScreen({ storeUrl, installed, required, platform: plat }) {
  const storeLabel = plat === "ios" ? "App Store" : "Google Play";
  return (
    <div
      data-testid="force-update-gate"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[9999] bg-[#0E1F16] text-white flex flex-col justify-between px-6 pt-16 pb-10"
    >
      <div className="flex flex-col items-center text-center max-w-md mx-auto">
        <div className="w-16 h-16 rounded-full bg-[#D67B54] flex items-center justify-center shadow-lg">
          <ArrowUpCircle className="w-8 h-8 text-white" />
        </div>
        <div className="mt-8 text-[11px] uppercase tracking-widest text-[#D67B54] font-semibold inline-flex items-center gap-1.5">
          <AlertTriangle className="w-3 h-3" /> Update required
        </div>
        <h1
          className="mt-3 text-3xl sm:text-4xl tracking-tighter font-medium"
          style={{ fontFamily: "Outfit" }}
        >
          A newer version of Binshare is available
        </h1>
        <p className="mt-4 text-sm text-white/70 leading-relaxed">
          You&apos;re on version <span data-testid="force-update-installed" className="text-white font-medium">{installed || "—"}</span>.
          Version <span data-testid="force-update-required" className="text-white font-medium">{required}</span> or later
          is now required. Please update from the {storeLabel} to keep using the app.
        </p>
      </div>
      <div className="max-w-md w-full mx-auto space-y-3">
        <Button
          data-testid="force-update-cta"
          onClick={() => storeUrl && openExternal(storeUrl)}
          className="w-full rounded-full bg-[#D67B54] hover:bg-[#c26e49] text-white min-h-[52px] text-base"
        >
          Update on {storeLabel}
        </Button>
        <p className="text-center text-[11px] text-white/50">
          Signed in as usual — your data will be right where you left it after the update.
        </p>
      </div>
    </div>
  );
}

function SoftUpdateBanner({ storeUrl, installed, latest, platform: plat }) {
  // Dismissal is keyed to `latest_version` so a fresh release re-shows
  // the nudge automatically without any extra plumbing.
  const dismissKey = `binshare.soft-update-dismissed:${plat}:${latest}`;
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(dismissKey) === "1";
  });
  if (dismissed) return null;
  const storeLabel = plat === "ios" ? "App Store" : "Google Play";
  const dismiss = () => {
    try { window.localStorage.setItem(dismissKey, "1"); } catch { /* noop */ }
    setDismissed(true);
  };
  return (
    <div
      data-testid="soft-update-banner"
      role="status"
      className="sticky top-0 z-[9998] bg-[#1B3624] text-white shadow-md"
    >
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <ArrowUpCircle className="w-4 h-4 text-[#D67B54] shrink-0" />
          <p className="text-[13px] leading-snug truncate">
            <span className="font-medium">A new version of Binshare is out.</span>{" "}
            <span className="text-white/70">
              You&apos;re on <span data-testid="soft-update-installed">{installed || "—"}</span> — latest is{" "}
              <span data-testid="soft-update-latest">{latest}</span>.
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            data-testid="soft-update-cta"
            onClick={() => storeUrl && openExternal(storeUrl)}
            className="rounded-full bg-[#D67B54] hover:bg-[#c26e49] text-white text-[12px] font-medium px-3.5 py-1.5"
          >
            Update on {storeLabel}
          </button>
          <button
            type="button"
            data-testid="soft-update-dismiss"
            onClick={dismiss}
            aria-label="Dismiss update banner"
            className="p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Return true when `installed` is strictly less than `required`,
 * comparing dotted numeric segments left-to-right. Non-numeric suffixes
 * ("1.0.0-beta") are stripped before compare. Missing segments default
 * to 0 ("1.0" == "1.0.0").
 */
export function isVersionBelow(installed, required) {
  const norm = (v) => String(v || "0")
    .split(/[+-]/)[0]
    .split(".")
    .map((s) => Number.parseInt(s, 10))
    .map((n) => (Number.isFinite(n) ? n : 0));
  const a = norm(installed);
  const b = norm(required);
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const ai = a[i] ?? 0;
    const bi = b[i] ?? 0;
    if (ai < bi) return true;
    if (ai > bi) return false;
  }
  return false;
}
